import {ObjUtil, Time, TimeInterval} from 'yougile-util'
import {DataObjectId, Hub, TimerStickerValue} from 'yougile-model'
import {Sync} from '@client/sync'
import {Board} from '@extensions/board'
import {globals} from '@extensions/globals-base'
import {StopwatchCtl} from '@extensions/sticker/controllers/stopwatch-ctl'
import {
  ColumnTime,
  ColumnTimeInterval,
  TimeIntervalHistory
} from './utils-interfaces'

const areIntervalIncludes = (
  baseInterval: TimeIntervalHistory,
  innerInterval: TimeIntervalHistory
): boolean => {
  return (
    innerInterval.begin >= baseInterval.begin &&
    innerInterval.begin <= baseInterval.end &&
    innerInterval.end >= baseInterval.begin &&
    innerInterval.end <= baseInterval.end
  )
}

const getDefaultColumnTime = (
  task: Hub,
  timerStickerValue: TimerStickerValue
): ColumnTime[] => {
  const box = Sync.getCurrentSandbox()
  const currentColumn = box.getHub(task.location)
  const stopIntervals = getStopIntervals(task, timerStickerValue)
  const pauseTime: number[] = []
  const start =
    timerStickerValue.history.length === 0
      ? timerStickerValue.current.timestamp
      : timerStickerValue.history[0].timestamp

  stopIntervals.forEach(timerState => {
    const areOverlapping = areIntervalIncludes(
      {begin: start, end: Time.now()},
      {begin: timerState.begin, end: timerState.end}
    )
    if (areOverlapping) {
      const diff = timerState.end - timerState.begin
      pauseTime.push(diff)
    }
  })
  const pauseTimeSum = pauseTime.reduce((a, b) => a + b, 0)
  const end = Time.now() - pauseTimeSum

  return [
    {
      column: currentColumn,
      time: getInterval(start, end),
      diff: end - start
    }
  ]
}

export const getInterval = (begin: number, end: number): string => {
  const timeInterval = TimeInterval.fromDates(begin, end).toString()
  const stopwatchCtl = new StopwatchCtl()

  return stopwatchCtl.formatTime({
    time: timeInterval, // e.g.: 0w1d5h0m0s
    by: globals.currentUserId, // used as a stub
    running: false, // used as a stub
    timestamp: Time.now() // used as a stub
  })
}

const getLocation = (task: Hub, time: number): DataObjectId => {
  const hist = task.getLocHistory()
  return ObjUtil.keys(hist).find(key => {
    return hist[key].includes(time)
  }) as DataObjectId
}

const moveTime = (task: Hub): number[] => {
  const hist = task.getLocHistory()
  const moveTimes = new Set<number>()

  for (const loc of ObjUtil.values(hist)) {
    for (const time of loc) {
      if (time) {
        moveTimes.add(time)
      }
    }
  }

  if (!moveTimes.has(task.timestamp)) {
    moveTimes.add(task.timestamp)
  }

  return Array.from(moveTimes).sort()
}

const getTimerStatesIntervals = (
  timerStickerValue: TimerStickerValue
): TimeIntervalHistory[] => {
  const timerStatesIntervals: TimeIntervalHistory[] = []
  const timerStates = [...timerStickerValue.history, timerStickerValue.current]
  timerStates.forEach((ts, ix) => {
    if (!ts.running) {
      const time =
        timerStates.length > ix + 1
          ? {begin: ts.timestamp, end: timerStates[ix + 1].timestamp}
          : {begin: ts.timestamp, end: Time.now()}
      timerStatesIntervals.push(time)
    }
  })
  return timerStatesIntervals
}

const getStopIntervals = (
  task: Hub,
  timerStickerValue: TimerStickerValue
): TimeIntervalHistory[] => {
  const timerStatesIntervals = getTimerStatesIntervals(timerStickerValue)
  const moveTimes = moveTime(task)
  const stopIntervals: TimeIntervalHistory[] = []
  const taskCreation = moveTimes[0]
  const stickerCreation =
    timerStickerValue.history.length === 0
      ? timerStickerValue.current.timestamp
      : timerStickerValue.history[0].timestamp

  stopIntervals.push({
    begin: taskCreation,
    end: stickerCreation
  })

  timerStatesIntervals.forEach(time => {
    const timeIndex = moveTimes.findIndex(t => t >= time.begin && t <= time.end)
    if (timeIndex === -1) {
      stopIntervals.push({
        begin: time.begin,
        end: time.end
      })
    } else {
      for (const [index, moveTime] of moveTimes.entries()) {
        const prev = moveTimes[index - 1]
        const next = moveTimes[index + 1]
        if (moveTime >= time.begin && moveTime <= time.end) {
          const isPrev = prev >= time.begin && prev <= time.end
          const isNext = next >= time.begin && next <= time.end
          stopIntervals.push({
            begin: isPrev ? prev : time.begin,
            end: moveTimes[index]
          })
          stopIntervals.push({
            begin: moveTimes[index],
            end: isNext ? next : time.end
          })
        }
      }
    }
  })

  return stopIntervals.filter(
    (value, index, self) =>
      index ===
      self.findIndex(t => t.begin === value.begin && t.end === value.end)
  )
}

const getPauseTime = (
  stopIntervals: TimeIntervalHistory[],
  moveTime: number,
  nextTime: number
): number[] => {
  const pauseTime: number[] = []
  const taskCreation = stopIntervals[0].begin
  const stickerCreation = stopIntervals[0].end
  for (const stopInterval of stopIntervals) {
    const areBeforeStickerCreation = areIntervalIncludes(
      {begin: taskCreation, end: stickerCreation},
      {begin: stopInterval.begin, end: stopInterval.end}
    )
    if (areBeforeStickerCreation) {
      continue
    }
    const areIncludes = areIntervalIncludes(
      {begin: moveTime, end: nextTime},
      {begin: stopInterval.begin, end: stopInterval.end}
    )
    if (areIncludes) {
      const diff = stopInterval.end - stopInterval.begin
      pauseTime.push(diff)
    }
  }
  return pauseTime
}

export const getColumnTimeInterval = (
  task: Hub,
  timerStickerValue: TimerStickerValue
): ColumnTimeInterval[] => {
  const box = Sync.getCurrentSandbox()
  const columnTimeInterval: ColumnTimeInterval[] = []
  const stopIntervals = getStopIntervals(task, timerStickerValue)
  const moveTimes = moveTime(task)

  for (const [index, moveTime] of moveTimes.entries()) {
    const nextTime = moveTimes[index + 1] ?? Time.now()

    const location = getLocation(task, moveTime) || task.location

    const column = box.getHub(location)
    const taskCreation = stopIntervals[0].begin
    const stickerCreation = stopIntervals[0].end

    const areBeforeStickerCreation = areIntervalIncludes(
      {begin: taskCreation, end: stickerCreation},
      {begin: moveTime, end: nextTime}
    )

    if (areBeforeStickerCreation) {
      continue
    }

    const pauseTime = getPauseTime(stopIntervals, moveTime, nextTime)
    const pauseTimeSum = pauseTime.reduce((a, b) => a + b, 0)

    let timeDiff = 0
    const areInCreationRange =
      stickerCreation >= moveTime && stickerCreation <= nextTime
    if (areInCreationRange) {
      const diff = stickerCreation - moveTime
      timeDiff = nextTime - moveTime - diff
    } else {
      timeDiff = nextTime - moveTime
    }

    const time = moveTime + timeDiff - pauseTimeSum

    if (columnTimeInterval.some(e => e.column?.id === location)) {
      const colTimeIndex = columnTimeInterval.findIndex(
        obj => obj.column?.id === location
      )
      const timeEnd = columnTimeInterval[colTimeIndex].timeEnd
      const time = timeEnd + timeDiff - pauseTimeSum
      columnTimeInterval[colTimeIndex].timeEnd = time
    } else {
      columnTimeInterval.push({
        column,
        timeBegin: moveTime ?? Time.now(),
        timeEnd: time
      })
    }
  }

  return columnTimeInterval
}

export const getColumnTime = (
  columnId: DataObjectId,
  columnTimeInterval: ColumnTimeInterval[]
): string => {
  const time = columnTimeInterval.find(el => el.column?.id === columnId)
  if (time?.timeBegin && time?.timeEnd) {
    return getInterval(time.timeBegin, time.timeEnd)
  }

  return getInterval(Time.now(), Time.now())
}

const getColumnsTime = (
  columnTimeInterval: ColumnTimeInterval[],
  columns: Hub[]
): ColumnTime[] => {
  const columnsTime: ColumnTime[] = []
  for (const column of columns) {
    const time = columnTimeInterval.find(el => el.column?.id === column.id)
    if (time?.timeBegin && time?.timeEnd) {
      columnsTime.push({
        column,
        time: getInterval(time.timeBegin, time.timeEnd),
        diff: time.timeEnd - time.timeBegin
      })
    }
  }

  return columnsTime
}

export const getColumns = (
  board: Hub,
  task: Hub,
  timerStickerValue: TimerStickerValue
): ColumnTime[] => {
  const hist = task.getLocHistory()
  const isEmpty = ObjUtil.isEmpty(hist)
  if (isEmpty) {
    return getDefaultColumnTime(task, timerStickerValue)
  }
  const columnTimeInterval = getColumnTimeInterval(task, timerStickerValue)
  const columns = Board.listColumns(board, {sorted: true})

  return getColumnsTime(columnTimeInterval, columns)
}

export const getColumnInterval = (
  columnId: DataObjectId,
  task: Hub,
  timerStickerValue: TimerStickerValue
): string => {
  const columnTimeInterval = getColumnTimeInterval(task, timerStickerValue)

  return getColumnTime(columnId, columnTimeInterval)
}

export const getColumnsTimeSum = (columnsTime: ColumnTime[]): string => {
  const now = Time.now()
  const timeSum = columnsTime.reduce((a, b) => a + b.diff, 0)

  return getInterval(now, now + timeSum)
}
